import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { showErrorToast } from '../utils/errorHandler';
import { Eye, EyeOff } from 'lucide-react';

const Auth = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phone || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    if (mode === 'signup' && !formData.name) {
      toast({
        title: 'Error',
        description: 'Please enter your name',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone format (9 digits without prefix)
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length !== 9) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid 9-digit Zambian phone number',
        variant: 'destructive',
      });
      return;
    }
    const fullPhone = `+260${cleanPhone}`;

    try {
      const api = (await import('../utils/api')).default;
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/signup';
      const payload = mode === 'login' 
        ? { phone: fullPhone, password: formData.password }
        : { name: formData.name, phone: fullPhone, email: formData.email || null, password: formData.password };
      
      const response = await api.post(endpoint, payload);
      
      // Store token and user data
      localStorage.setItem('tekstar_token', response.data.token);
      localStorage.setItem('tekstar_user', JSON.stringify(response.data.user));

      toast({
        title: 'Success',
        description: mode === 'login' ? 'Welcome back!' : 'Account created successfully!',
      });

      // Check if there's a redirect URL stored
      const redirectUrl = sessionStorage.getItem('redirect_after_login');
      if (redirectUrl) {
        sessionStorage.removeItem('redirect_after_login');
        navigate(redirectUrl);
        return;
      }

      // Redirect to previous page or home
      const from = location.state?.from || '/';
      if (from === '/' && location.state?.pickup && location.state?.dropoff) {
        localStorage.setItem('ride_pickup', location.state.pickup);
        localStorage.setItem('ride_dropoff', location.state.dropoff);
        navigate('/select-ride');
      } else {
        navigate(from);
      }
    } catch (error) {
      console.error('Signup/Login error:', error);
      showErrorToast(toast, error, 'Authentication failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center px-4 pt-20 pb-8">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {mode === 'login' ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-gray-600">
            {mode === 'login' ? 'Log in to continue' : 'Sign up to get started'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
              <Input
                id="phone"
                type="tel"
                className="rounded-l-none"
                placeholder="XXX XXX XXX"
                maxLength={9}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                required
                data-testid="phone-input"
              />
            </div>
          </div>

          {mode === 'signup' && (
            <div className="space-y-2">
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <p className="text-xs text-gray-500">For notifications and receipts</p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              {mode === 'login' && (
                <Link to="/forgot-password" className="text-sm text-pink-600 hover:text-pink-700">
                  Forgot password?
                </Link>
              )}
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold"
          >
            {mode === 'login' ? 'Log in' : 'Sign up'}
          </Button>
        </form>

        <div className="mt-6 space-y-3">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => navigate(mode === 'login' ? '/signup' : '/login')}
                className="text-pink-600 hover:text-pink-700 font-medium"
              >
                {mode === 'login' ? 'Sign up' : 'Log in'}
              </button>
            </p>
          </div>
          
          {mode === 'login' && (
            <div className="text-center pt-3 border-t">
              <p className="text-sm text-gray-600">
                Are you a driver?{' '}
                <Link to="/driver/login" className="text-purple-600 hover:text-purple-700 font-medium">
                  Sign in here
                </Link>
              </p>
            </div>
          )}
        </div>
        
        {/* Legal Links */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>By continuing, you agree to our</p>
          <p className="mt-1">
            <Link to="/terms" className="text-pink-600 hover:text-pink-700">Terms of Service</Link>
            {' '}&{' '}
            <Link to="/privacy" className="text-pink-600 hover:text-pink-700">Privacy Policy</Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Auth;